(function () {

  function stopEvent (evt) {
    evt.stopPropagation()
    evt.preventDefault()
  }

  function debounce (callback, timeout = 300) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        callback.apply(this, args)
      }, timeout)
    }
  }

  // ref: https://dev.to/uttarasriya/js-polyfill-part-4-debounce-throttle-leading-trailing-options-3nn8
  function throttle (func, delay, options = { leading: true, trailing: true }) {
    let timer = null,
        lastRan = null,
        trailingArgs = null

    return function (...args) {

      if (timer) { // called within cooldown period
        lastRan = this // update context
        trailingArgs = args // save for later
        return
      }

      if (options.leading) { // if leading
        func.call(this, ...args) // call the 1st instance
      } else { // else it's trailing
        lastRan = this // update context
        trailingArgs = args // save for later
      }

      const coolDownPeriodComplete = () => {
        if (options.trailing && trailingArgs) { // if trailing and the trailing args exist
          func.call(lastRan, ...trailingArgs) // invoke the instance with stored context "lastRan"
          lastRan = null // reset the status of lastRan
          trailingArgs = null // reset trailing arguments
          timer = setTimeout(coolDownPeriodComplete, delay) // clear the timout
        } else {
          timer = null // reset timer
        }
      }

      timer = setTimeout(coolDownPeriodComplete, delay)
    }
  }

  function onVisible (element, callback, options = {}) {
    const canScroll = ('onscroll' in window) && !(/glebot/.test(navigator.userAgent))
    if (canScroll) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback()

            observer.disconnect()
          }
        })
      }, options)

      observer.observe(element)
    } else {
      callback()
    }
  }

  function onResize (element, callback) {
    const resizeObserver = new ResizeObserver(callback)
    resizeObserver.observe(element)
  }

  const domReady = new Promise(resolve => {
    document.addEventListener('DOMContentLoaded', resolve)
  })

  let dependencies = {}
  let dependencyMap = JSON.parse(document.querySelector('#async-dependencies').innerHTML)

  function loadDependency (id) {
    return dependencies[id] = dependencies[id] ?? new Promise(resolve => {
      let loader
      switch (id.split('-').pop()) {
        case 'js':
          loader = document.createElement('script')
          loader.src = dependencyMap[id]
          loader.async = true
          break
        case 'css':
          loader = document.createElement('link')
          loader.href = dependencyMap[id]
          loader.rel = 'stylesheet'
          loader.type = 'text/css'
          loader.media = 'all'
          break
      }

      loader.id = id
      loader.onload = resolve
      document.body.appendChild(loader)
    })
  }

  function loadDependencies (ids) {
    return Promise.all(ids.map(id => loadDependency(id)))
  }

  ;(() => {
    class CartUpdate {
      count = window['femtis']?.cart?.itemCount

      constructor () {
        document.documentElement.addEventListener('wetheme-cart-update', this.check.bind(this))
        document.documentElement.addEventListener('wetheme-toggle-right-drawer', this.check.bind(this))
      }

      check () {
        fetch(`${window.Shopify?.routes?.root}cart?view=discount`)
          .then(response => response.json())
          .then(response => {
            const total = response.items.reduce(
              (accumulator, item) => accumulator + (
                item['count_for_discount'] ? item.quantity : 0
              ), 0,
            )

            if (this.count !== total) {
              this.count = total

              document.documentElement.dispatchEvent(new CustomEvent('femtis-cart-discount', {
                detail: {
                  itemCount: total,
                },
              }))
            }
          })
      }
    }

    window.femtisCartDiscount = new CartUpdate()
  })();

  ;(function () {
    class FemtisImage extends HTMLElement {

      connectedCallback () {
        this.init()
      }

      init () {
        const selector = this.hasAttribute('selector')
          ? this.getAttribute('selector')
          : 'img'

        this.imgElement = this.querySelector(selector)
        this.isBackground = this.imgElement instanceof HTMLImageElement

        onVisible(this.imgElement, this.load.bind(this))
      }

      load () {
        const size = this.imgElement.clientWidth

        this.isBackground
          ? this.loadImage(size)
          : this.loadImageBackground(size)
      }

      loadImage (size) {
        this.imgElement.sizes = size + 'px'
        const toSizeString = (size) => `width=${size} ${size}w`

        this.imgElement.srcset = [
          this.imgElement.dataset.lazyimageTmpl.replaceAll('width=111 111w', toSizeString(size)),
          this.imgElement.dataset.lazyimageTmpl.replaceAll('width=111 111w', toSizeString(size * 2)),
          this.imgElement.dataset.lazyimage,
        ].filter(Boolean).join(', ')

        delete this.imgElement.dataset.lazyimage
        delete this.imgElement.dataset.lazyimageTmpl
      }

      loadImageBackground (size) {
        const styles = getComputedStyle(this.imgElement)

        this.imgElement.style.backgroundImage = styles.backgroundImage
          .replace(/width=\d+/, 'width=' + size)
      }
    }

    customElements.define('femtis-image', FemtisImage)
  })();

  (function () {
    class FemtisSlider extends HTMLElement {

      connectedCallback () {
        this.firstOfKind = document.querySelector('femtis-slider') === this
        this.link = this.parentNode // a.grid__image

        this.init()
      }

      init () {
        this.maybeReorder()

        this.images = this.querySelectorAll('.responsive-image-wrapper')

        let bullets = ''
        let paginationIndex = 0
        for (const image of this.images) {
          image.style.order = ''
          bullets += `<button class="glide__bullet ${paginationIndex === 0 ? 'glide__bullet--active' : ''}" data-glide-dir="=${paginationIndex}"></button>`
          paginationIndex++
        }

        this.innerHTML = `<div class="glide__track" data-glide-el="track">
          <ul class="glide__slides">
            ${this.innerHTML}
          </div>
        </div>
        <div class="glide__arrows" data-glide-el="controls">
          <button class="glide__arrow glide__arrow--left" data-glide-dir="<"></button>
          <button class="glide__arrow glide__arrow--right" data-glide-dir=">"></button>
        </div>
        <div class="glide__bullets" data-glide-el="controls[nav]">
          ${bullets}
        </div>`

        const timestamp = () => (new Date()).getTime()

        this.mouseDownAt = 0
        this.link.addEventListener('mousedown', (evt) => {
          this.mouseDownAt = timestamp()
        })
        this.link.addEventListener('click', (evt) => {
          // ignore click that dragged more than0 200ms
          if (timestamp() - 200 > this.mouseDownAt) {
            stopEvent(evt)
          }
        })

        this.querySelectorAll('.glide__bullet, .glide__arrow')
          .forEach(el => {
            el.addEventListener('click', stopEvent)
            el.addEventListener('mouseup', stopEvent)
            el.addEventListener('touchend', stopEvent)
          })

        onVisible(this, this.setup.bind(this))

        if (this.firstOfKind) {
          onVisible(this, () => {
            setTimeout(() => {
              this.classList.add('f--instruction-anim')
            }, 2000)
          }, { threshold: .8 })
        }
      }

      maybeReorder () {
        this.moveToNthPosition(this.querySelector('.f--img-primary'), 0)
        this.moveToNthPosition(this.querySelector('.f--img-secondary'), 1)
      }

      moveToNthPosition (elem, n) {
        // not found
        if (!elem) { return }

        // already at correct position
        if (Array.from(elem.parentNode.children).indexOf(elem) === n) { return }

        elem.parentNode.insertBefore(elem, elem.parentNode.children[n])
      }

      setup () {
        domReady
          .then(() => loadDependencies(['glide-js', 'glide-css']))
          .then(() => {
            this.style.width = this.clientWidth + 'px'
            this.style.overflow = 'hidden'

            onResize(this.parentNode, () => {
              this.style.width = this.parentNode.clientWidth + 'px'
            }, true)

            this.glide = new Glide(this, {
              type: 'carousel',
              animationDuration: 500,
              breakpoints: {
                767: {
                  type: 'slider',
                  rewind: false,
                },
              },
            })

            this.glide.mount()
          })
      }
    }

    customElements.define('femtis-slider', FemtisSlider)
  })();

  (function () {
    class FemtisCoreBenefits extends HTMLElement {

      connectedCallback () {
        this.init()
      }

      init () {
        this.matcher = matchMedia('screen and (max-width: 991px)')
        this.check()
        this.matcher.addEventListener('change', this.check.bind(this))
      }

      check () {
        if (this.matcher.matches) {
          this.setup()
        } else {
          this.destruct()
        }
      }

      setup () {
        domReady
          .then(() => loadDependencies(['glide-js', 'glide-css']))
          .then(() => {
            if (this.glide) {
              return
            }

            this.glide = new Glide(this, {
              type: 'carousel',
              perView: 1,
              loop: true,
              autoplay: 3000,
            })

            this.glide.mount()
          })
      }

      destruct () {
        this.glide?.destroy()
        this.glide = undefined
      }
    }

    customElements.define('femtis-core-benefits', FemtisCoreBenefits)
  })();

  (function () {
    class FemtisQuickBuy extends HTMLElement {
      connectedCallback () {
        this.type = this.getAttribute('type')
        this.init()
      }

      init () {
        this.template = this.querySelector('.f-overlay-tmpl').content.cloneNode(true)
        this.successTmpl = this.querySelector('.f-success-tmpl').content.cloneNode(true)

        this.template.querySelector('.f-product-box').addEventListener('click', e => e.stopPropagation())
        this.template.querySelector('.f-product-quick-add').addEventListener('click', this.close.bind(this))
        this.template.querySelector('.f-product-box-close').addEventListener('click', this.close.bind(this))

        this.template.querySelectorAll('input.f--option1')
          .forEach(input => {
            input.addEventListener('change', async () => {
              await this.addToCart()
            })
            input.parentNode.addEventListener('click', (evt) => {
              evt.stopPropagation()
            })
          })

        this.toggle = this.querySelector('.f-toggle')
        this.toggle.addEventListener('click', evt => {
          stopEvent(evt)
          this.open()
        })
      }

      open () {
        const isMobile = matchMedia('screen and (max-width: 600px)').matches

        this.toggle.classList.add('f--open')
        document.body.classList.toggle('f--quickbuy-type-overlay', this.type === 'overlay')
        document.body.classList.toggle('f--quickbuy-type-default', this.type === 'default')
        document.body.classList.toggle('f--quickbuy-is-mobile', isMobile)
        document.body.classList.toggle('f--quickbuy-is-desktop', !isMobile)

        if (isMobile || this.type === 'overlay') {
          document.body.appendChild(this.template)
        } else {
          this.parentNode.appendChild(this.template)
        }

        this.template = this.getTemplate()
      }

      getTemplate () {
        const elem = this.parentNode.querySelector('.f-product-quick-add')
        return elem ? elem : document.querySelector('.f-product-quick-add')
      }

      async addToCart () {
        const activeEl = this.template.querySelector('.f--option1:checked')
        this.template.querySelectorAll('.f--option1')
          .forEach(el => el.disabled = true)

        this.sending = true

        await window.fetch(`${window.Shopify?.routes?.root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            form_type: 'product',
            quantity: 1,
            'id': activeEl.value,
            'product-id': this.template.querySelector('[name="product-id"]').value,
          })
        })

        this.template.querySelectorAll('.f--option1').forEach(el => {
          el.disabled = false
          el.checked = false
        })

        this.sending = false

        activeEl.classList.add('f--done')
        activeEl.parentNode.classList.add('f--done')

        setTimeout(async () => {
          await this.afterSubmit(activeEl)
        }, 1000)
      }

      async afterSubmit (activeEl) {
        const cart = await (await fetch(`${window.Shopify?.routes?.root}cart.js`)).json()

        if (matchMedia('screen and (max-width: 600px)').matches || this.type === 'overlay') {
          window.wetheme.updateCartDrawer(cart)
          window.wetheme.toggleRightDrawer('cart', false, {cart: cart})

          document.body.appendChild(this.successTmpl)
          this.successTmpl = document.querySelector('.f-product-quick-add-success')

          setTimeout(() => {
            this.successTmpl.classList.add('f--exiting')

            setTimeout(() => {
              this.successTmpl.classList.remove('f--exiting')
              this.successTmpl.parentNode.removeChild(this.successTmpl)
            }, 500)
          }, 2000)
        } else {
          window.wetheme.toggleRightDrawer('cart', true, {cart: cart})
        }

        activeEl.classList.remove('f--done')
        activeEl.parentNode.classList.remove('f--done')
        this.close()
      }

      close () {
        if (this.sending) {
          return
        }

        this.toggle.classList.remove('f--open')

        let elem = this.getTemplate()

        elem.classList.add('f--exiting')
        setTimeout(() => {
          elem.classList.remove('f--exiting')
          elem.parentNode.removeChild(elem)
          this.template = elem
        }, 500)
      }
    }

    customElements.define('femtis-quick-buy', FemtisQuickBuy)
  })();

  (function () {
    class FemtisProductVideo extends HTMLElement {
      connectedCallback () {
        this.init()
      }

      init () {
        this.querySelectorAll('video')
          .forEach(video => {
            const observer = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  video.play()
                } else {
                  video.pause()
                }
              })
            }, {
              threshold: 0.1,
            })

            observer.observe(video)
          })
      }
    }

    customElements.define('femtis-product-video', FemtisProductVideo)
  })();

  (function () {
    class TrustedShopsReviewPreview extends HTMLElement {
      static observedAttributes = ['identifier', 'type']
      grade = false
      state = 'loading'

      connectedCallback () {
        this.init()
      }

      init () {
        const encodedIdentifier = this.getEncodedIdentifier(this.getAttribute('identifier'))
        // DE: chl-de139a16-090c-455c-a66d-b1149a6bbea8
        // DE: chl-e8e14a08-4cc5-4558-8a06-166a1e9ec6c4
        fetch(`https://integrations.etrusted.com/feeds/grades/v1/channels/chl-de139a16-090c-455c-a66d-b1149a6bbea8/products/sku/${encodedIdentifier}/feed.json`)
          .then(response => response.json())
          .then(response => {
            if (response.grades) {
              this.grade = response.grades.overall
              this.state = 'loaded'
            } else {
              this.grade = false
              this.state = 'empty'
            }

            this.render()
          })

        this.render()
      }

      // source
      // https://integrations.etrusted.com/applications/product-star-widget/v1
      getEncodedIdentifier (rawIdentifier) {
        const e = unescape(encodeURIComponent(rawIdentifier))
        let s = ''
        for (let t = 0; t < e.length; t++) {
          s += e.charCodeAt(t).toString(16)
        }
        return s
      }

      render () {
        let star = `<svg class="f-star f--full" width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="f--path" d="M7.5 0L9.18386 5.18237H14.6329L10.2245 8.38525L11.9084 13.5676L7.5 10.3647L3.09161 13.5676L4.77547 8.38525L0.367076 5.18237H5.81614L7.5 0Z"/>
        </svg>`

        if (this.getAttribute('type') === 'full') {
          let star_empty = `<svg class="f-star f--empty" xmlns="http://www.w3.org/2000/svg" width="12.036" height="11.336" viewBox="0 0 12.036 11.336">
            <path class="f--path" d="M2.43,11.336v0l1.5-4.621L0,3.854H4.861L6.114,0,7.242,3.854h4.793L8.163,7,9.6,11.332,6.114,8.822,2.433,11.335ZM6.089,8.2h0l2.586,1.865L7.428,6.817l3.057-2.31H6.926l-.837-2.86-.931,2.86H1.551L4.593,6.542,3.357,10.061,6.089,8.2Z"/>
          </svg>`


          let percentage = this.state === 'loaded'
            ? ((this.grade.rating / 5) * 100).toFixed(2)
            : '0'

          star = `<div class="f-star-frame" style="--star-percentage: ${percentage}%">
            <div class="f-star-list f-inactive"><div class="f-star-list-inner">${star_empty}${star_empty}${star_empty}${star_empty}${star_empty}</div></div>
            <div class="f-star-list f-active"><div class="f-star-list-inner">${star}${star}${star}${star}${star}</div></div>
          </div>`
        }

        this.classList.toggle('f--loading', this.state === 'loading')
        this.classList.toggle('f--loaded', this.state === 'loaded')
        this.classList.toggle('f--empty', this.state === 'empty')

        this.classList.toggle('f--type-compact', this.getAttribute('type') === 'compact')
        this.classList.toggle('f--type-full', this.getAttribute('type') === 'full')

        switch (this.state) {
          case 'loading':
            this.innerHTML = `
              ${star}
              <div class="f-review-value"></div>
            `
            break
          case 'loaded':
            this.innerHTML = `
              ${star}
              <div class="f-review-value">${this.grade.rating.toFixed(2)}</div>
            `
            break
          case 'empty':
            this.innerHTML = `
              ${star}
              <div class="f-review-value">-</div>
            `
            break
        }
      }
    }

    customElements.define('femtis-trusted-shops-review-preview', TrustedShopsReviewPreview)
  })();

  (function () {
    class ProductSecurity extends HTMLElement {
      connectedCallback () {
        this.init()
      }

      init () {
        this.placeholders = this.querySelectorAll('[data-placeholder]')
        this.contents = this.querySelectorAll('[data-content]')

        document.body.addEventListener('femtis:product:variant:selected', event => {
          this.switch(event.detail.variant, event.detail.product)
        })
      }

      switch (variant, product) {
        this.querySelectorAll('[data-content-clone]').forEach(el => el.remove())

        if (!variant) {
          this.placeholders.forEach(el => el.style.display = 'inline')
        } else {
          this.placeholders.forEach(el => el.style.display = 'none')
          this.contents.forEach(ogEl => {
            const el = ogEl.cloneNode(true)
            el.style.display = 'inline'
            el.setAttribute('data-content-clone', true)
            el.innerHTML = el.innerHTML.replace('%ean%', variant.barcode ? variant.barcode : this.dataset.fallbackSnippet)

            ogEl.parentNode.insertBefore(el, ogEl)
          })
        }
      }
    }

    customElements.define('femtis-product-security', ProductSecurity)
  })();

  (function () {
    class FemtisSuctionPowerTrigger extends HTMLElement {
      modal = undefined
      suctionPower = undefined

      connectedCallback () {
        this.init()

        // Make it focusable
        if (!this.hasAttribute('tabindex')) {
          this.setAttribute('tabindex', '0')
        }

        // Add ARIA role
        if (!this.hasAttribute('role')) {
          this.setAttribute('role', 'button')
        }

        // Handle keyboard interaction
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            this.querySelector('[data-trigger]').click()
          }
        })
      }

      init () {
        this.template = this.querySelector('script').innerHTML
        this.querySelector('[data-trigger]')
          .addEventListener('click', this.render.bind(this))
      }

      render () {
        this.suctionPower = this.getAttribute('suction-power')
        this.modal = document.createElement('div')
        this.modal.innerHTML = this.template
        document.body.appendChild(this.modal)

        if (this.suctionPower) {
          const tableFrame = this.modal.querySelector('.f-modal-table-border')
          const tr = tableFrame.parentNode.querySelector(`tbody tr:nth-child(${this.suctionPower})`)

          const tableRect = tableFrame.parentNode.getBoundingClientRect()
          const trRect = tr.getBoundingClientRect()

          tableFrame.style.display = 'block'
          tableFrame.style.top = (trRect.top - tableRect.top) + 'px'
          tableFrame.style.left = (trRect.left - tableRect.left - 15) + 'px'
          tableFrame.style.width = (trRect.width + 30) + 'px'
          tableFrame.style.height = (trRect.height) + 'px'
        }

        this.modal.querySelector('[data-destruct]')
          .addEventListener('click', this.destroy.bind(this))

        this.modal
          .addEventListener('click', this.destroy.bind(this))

        this.modal.querySelector('.f-modal-content')
          .addEventListener('click', event => event.stopPropagation())
      }

      destroy () {
        if (this.modal) {
          const modal = this.modal
          this.modal = undefined

          modal.querySelector('.f-sidebar-panel').classList.add('f--exit')
          setTimeout(() => {
            modal.remove()
          }, 250)
        }

      }
    }

    customElements.define('femtis-suction-power-trigger', FemtisSuctionPowerTrigger)
  })();

  (function () {
    class FemtisProductQuantityDiscount extends HTMLElement {
      count = window.femtisCartDiscount.count
      discountLow = undefined
      discountHigh = undefined

      connectedCallback () {
        this.init()
      }

      init () {
        this.discountLow = this.querySelector('[data-discount-low]')
        this.discountHigh = this.querySelector('[data-discount-high]')

        this.update()

        document.documentElement.addEventListener('femtis-cart-discount', event => {
          this.count = event.details?.itemCount
          this.update()
        })
      }

      update () {
        this.discountLow.classList.toggle('f--reached', this.count > 2)
        this.discountLow.classList.toggle('f--secondary', this.count > 4)
        this.discountHigh.classList.toggle('f--reached', this.count > 4)
      }
    }

    customElements.define('femtis-product-quantity-discounts', FemtisProductQuantityDiscount)
  })();

  (function () {
    class FemtisProductReviewDetailsSlider extends HTMLElement {

      connectedCallback () {
        this.init()
      }

      init () {
        this.glideEl = this.querySelector('.f-reviews')
        this.glideEl.innerHTML = `<div class="glide__track" data-glide-el="track">
          <ul class="glide__slides">
            ${this.glideEl.innerHTML}
          </div>
        </div>`

        onVisible(this.glideEl, this.setup.bind(this))
      }

      setup () {
        this.querySelector('.f-reviews-arrow.f--next')
          .addEventListener('click', this.next.bind(this))

        this.querySelector('.f-reviews-arrow.f--prev')
          .addEventListener('click', this.prev.bind(this))

        this.build()
      }

      prev () {
        this.glide?.go('<')
      }

      next () {
        this.glide?.go('>')
      }

      build () {
        domReady
          .then(() => loadDependencies(['glide-js', 'glide-css']))
          .then(() => {
            this.style.width = this.clientWidth + 'px'
            this.style.overflow = 'hidden'

            onResize(this.parentNode, () => {
              this.style.width = this.parentNode.clientWidth + 'px'
            }, true)

            this.glideEl.classList.add('glide-init')
            this.glide = new Glide(this.glideEl, {
              type: 'slider',
              perView: 4,
              gap: 12,
              bound: true,
              breakpoints: {
                1400: {
                  perView: 3,
                },
                767: {
                  perView: 1,
                  gap: 8,
                },
              },
            })
            this.glide.mount()
          })
      }
    }

    customElements.define('femtis-product-review-details-slider', FemtisProductReviewDetailsSlider)
  })()

  ;(function () {
    class FemtisCollectionGrid extends HTMLElement {
      changeEl
      floatingPanelEl
      active
      activeProducts = []
      cardCheckboxes
      previewPanels = []
      detailActive
      detailPanelHeaderEL
      detailPanelHeaderFrameEL
      detailPanelEl
      detailElements
      payload
      imageEls
      animationParams

      connectedCallback () {
        this.payload = JSON.parse(this.getAttribute('payload'))
        this.init()
      }

      init () {
        const pageContainer = document.querySelector('#PageContainer')

        this.changeEl = this.querySelector('[data-compare-toggle]')
        this.changeEl.addEventListener('change', this.toggleCompare.bind(this))

        this.previewPanels = [
          this.querySelector('[data-panel="0"]'),
          this.querySelector('[data-panel="1"]'),
          this.querySelector('[data-panel="2"]'),
        ]

        this.detailPanelEl = this.querySelector('[data-compare-detail-panel]')
        this.detailPanelEl.querySelector('[data-unselect]')
            .addEventListener('click', () => this.reset(true, false, true))

        this.detailPanelHeaderEL = this.detailPanelEl.querySelector('.f-compare-detail-panel-header')
        this.detailPanelHeaderFrameEL = this.detailPanelEl.querySelector('.f-compare-detail-panel-header-frame')

        this.detailPanelEl.addEventListener('scroll', () => {
          requestAnimationFrame(this.runDetailAnimation.bind(this))
        }, { passive: true })

        this.detailElements = [
            this.loadDetailElements(0),
            this.loadDetailElements(1),
            this.loadDetailElements(2),
        ]

        this.floatingPanelEl = this.querySelector('[data-compare-floating-panel]')
        this.floatingPanelEl.querySelector('[data-close]').addEventListener('click', () => {
          this.reset(true, true, true)
        })
        this.floatingPanelToggleEl = this.floatingPanelEl.querySelector('[data-open]')
        this.floatingPanelToggleEl.addEventListener('click', this.toggleDetail.bind(this))

        this.cardCheckboxes = this.querySelectorAll('.f-compare-banner [type="checkbox"]')
        this.cardCheckboxes
          .forEach((elem) => {
            elem.addEventListener('change', () => {
              if (elem.checked) {
                this.activeProducts.push(this.productFromCheckbox(elem))
                this.updatePreview()
              } else {
                this.removeProduct(elem.value)
              }
            })
          })

        if (!this.cardCheckboxes.length) {
          this.changeEl.closest('.collection-product-compare-toggle').style.display = 'none'
        }

        pageContainer.append(this.detailPanelEl)
        pageContainer.append(this.floatingPanelEl)

        onResize(this.detailPanelEl, throttle(() => {
          this.calculateDetailAnimation()
        }, 500, { leading: false, trailing: true }))

        document.documentElement.addEventListener('wetheme-cart-update', () => {
          this.reset (true, true, true)
        })
      }

      updatePreview () {
        if (this.activeProducts.length > 2) {
          this.cardCheckboxes.forEach(c => c.disabled = !c.checked)
        } else {
          this.cardCheckboxes.forEach(c => c.disabled = false)
        }

        this.floatingPanelToggleEl.classList.toggle('e--inactive', !this.activeProducts.length)

        this.previewPanels.forEach((elem, index) => {
          const product = this.activeProducts[index]
          if (product) {
            elem.style.backgroundImage = `url(${product.image.src60})`
          } else {
            elem.style.backgroundImage = 'none'
          }
        })
      }

      productFromCheckbox (checkbox) {
        const cardEl = checkbox.closest('product-card')
        const compareBannerEl = checkbox.closest('.f-compare-banner')
        const imageEl = cardEl.querySelector('.grid__image')
        const imageSrc = imageEl.querySelector('femtis-slider .f--img-primary femtis-image img').src
        const productInfo = JSON.parse(compareBannerEl.dataset.productDetails)

        return {
          id: checkbox.value,
          href: imageEl.href,
          title: cardEl.querySelector('.product-grid--title [data-product-card-link]').innerText,
          titleHtml: cardEl.querySelector('.product-grid--title').outerHTML,
          priceHtml: cardEl.querySelector('[data-block-type="product_card_price"]').outerHTML,
          swatchesHtml: cardEl.querySelector('.color-items-wrapper').outerHTML,
          badgeHtml: cardEl.querySelector('.product-badges').outerHTML,
          data: checkbox.dataset.compare,
          sizes: productInfo.sizes,
          style: productInfo.style,
          materialType: productInfo.material_type,
          activities: productInfo.activities,
          suctionPowerLabel: productInfo.suction_power,
          suctionPowerDropHtml: cardEl.querySelector('.suction-power-drops').outerHTML,
          compareQuickBuyToggle: cardEl.querySelector('.f-quick-buy-compare .f-toggle'),
          formNode: cardEl.querySelector('.f-simple-atc-overlay'),
          image: {
            src: imageSrc.replace('&width=30', ''),
            src60: imageSrc.replace('&width=30', '&width=60'),
            src600: imageSrc.replace('&width=30', '&width=600'),
          },
        }
      }

      toggleCompare () {
        this.floatingPanelEl.style.display = this.changeEl.checked ? 'block' : 'none'
        this.classList.toggle('f--compare-active', this.changeEl.checked)
      }

      toggleDetail (next = !this.detailActive) {
        if (next && !this.activeProducts.length) {
          return
        }

        this.detailActive = next
        this.detailPanelEl.style.display = this.detailActive ? 'block' : 'none'
        this.classList.toggle('f--comparing', this.detailActive)
        document.body.classList.toggle('e--comparing-products', this.detailActive)

        if (this.detailActive) {
          this.renderDetail()
        }
      }

      renderDetail () {
        this.detailElements.forEach((elem, index) => {
          const product = this.activeProducts[index]
          if (product) {
            elem.imageFrame.innerHTML = `<div class="f-product-compare-image">
              <img src="${product.image.src600}" alt="${product.title}">
              ${product.badgeHtml}
              <div class="f-product-remove" data-product-remove></div>
            </div>`

            elem.imageFrame.querySelector('[data-product-remove]').addEventListener('click', () => {
              this.removeProduct(product.id)
            })

            elem.sizeFrame.innerHTML = `<div class="f-product-compare-value f-product-compare-sizes">
              ${product.sizes}
            </div>`

            elem.styleFrame.innerHTML = `<div class="f-product-compare-value f-product-compare-style">
              ${product.style}
            </div>`


            const materialInfo = this.payload['tech-preview'][product.materialType]
            const materialSections = Object.values(materialInfo).map(section => {
              const materials = section.material.split(',')

              return `
                <strong>${section.headline}</strong>
                <ul>
                  <li>${materials.join('</li><li>')}</li>
                </ul>
              `
            })

            elem.materialFrame.innerHTML = `<div class="f-product-compare-value f-product-compare-material">
              ${materialSections.join('')}
            </div>`

            elem.activitiesFrame.innerHTML = `<div class="f-product-compare-value f-product-compare-activities">
              <ul>
                <li>${product.activities.join('</li><li>')}</li>
              </ul>
            </div>`

            elem.suctionPowerFrame.innerHTML = `<div class="f-product-compare-value f-product-compare-suction-power">
              ${product.suctionPowerLabel}
              ${product.suctionPowerDropHtml}
            </div>`

            elem.headFrame.innerHTML = `<div class="f-product-compare-head">
              ${product.titleHtml}

              <div class="f-product-compare-head-subline">
                ${product.priceHtml}
                ${product.swatchesHtml}
              </div>

              <a href="#" class="f-btn f-btn-md">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M4.61523 12.3845L8.30754 16.0768" stroke="white" stroke-linecap="round"/>
                  <path d="M13.8462 8.69238L8.30773 16.077" stroke="white" stroke-linecap="round"/>
                  <path d="M2.22147 7.6712C2.3822 6.70683 3.21658 6 4.19425 6H15.0623C15.9997 6 16.8113 6.65106 17.0147 7.56614L19.4591 18.5661C19.7367 19.8152 18.7863 21 17.5068 21H2.36092C1.12504 21 0.184956 19.8903 0.388133 18.6712L2.22147 7.6712Z" fill="white"/>
                  <path d="M12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3" stroke="white" stroke-linecap="round"/>
                </svg>
              </a>
            </div>`

            elem.headFrame.querySelector('.f-btn').addEventListener('click', event => {
              event.preventDefault()
              product.compareQuickBuyToggle.dispatchEvent(new CustomEvent('click'))
            })
          } else {
            elem.imageFrame.innerHTML = `<div class="f-product-compare-image f--empty">
              <div class="f-product-add" data-product-add></div>
            </div>`

            elem.imageFrame.querySelector('[data-product-add]').addEventListener('click', () => {
              this.toggleDetail()
            })

            elem.sizeFrame.innerHTML = ''
            elem.styleFrame.innerHTML = ''
            elem.headFrame.innerHTML = ''
            elem.activitiesFrame.innerHTML = ''
            elem.materialFrame.innerHTML = ''
            elem.suctionPowerFrame.innerHTML = ''
          }
        })

        this.calculateDetailAnimation()
      }

      calculateDetailAnimation () {
        this.imageEls = this.detailPanelEl.querySelectorAll('[data-compare-image] .f-product-compare-image')
        const el = this.imageEls[0]

        if (!el) { return }

        const headerRect = document.body.querySelector('[data-wetheme-section-type="header"]').getBoundingClientRect()
        this.detailPanelEl.style.setProperty('top', (headerRect.top + headerRect.height - 1) + 'px')


        this.animationParams = {}

        el.style.setProperty('aspect-ratio', .75)
        el.style.setProperty('width', 'auto')
        el.style.setProperty('max-width', '100%')

        this.animationParams['maxHeight'] = el.clientHeight
        this.animationParams['maxWidth'] = el.clientWidth
        this.detailPanelHeaderFrameEL.style.removeProperty('height')
        this.detailPanelHeaderFrameEL.style.setProperty('height', this.detailPanelHeaderFrameEL.clientHeight + 'px')

        el.style.setProperty('aspect-ratio', 1)
        el.style.setProperty('max-width', '220px')

        this.animationParams['minHeight'] = el.clientHeight

        el.style.removeProperty('aspect-ratio')
        el.style.removeProperty('max-width')

        el.clientHeight // reflow

        this.animationParams['scrollRange'] = this.animationParams['maxHeight'] - this.animationParams['minHeight']

        requestAnimationFrame(this.runDetailAnimation.bind(this))
      }

      runDetailAnimation () {
        const scrollTop = this.detailPanelEl.scrollTop

        const done = scrollTop > this.animationParams['scrollRange']
        this.detailPanelEl.classList.toggle('e--scrolled', done)

        if (!done) {
          const percentage = 1 - (scrollTop / this.animationParams['scrollRange'])
          let aspectRatio = 1 - (.25 * percentage)
          let width = false
          if (this.animationParams['maxWidth'] > 220) {
            const widthRange = this.animationParams['maxWidth'] - 220
            width = (220 + (widthRange * percentage)) + 'px'
          }

          this.imageEls.forEach(img => {
            img.style.removeProperty('max-width')
            img.style.setProperty('aspect-ratio', aspectRatio)
            if (width) {
              img.style.setProperty('width', width)
            } else {
              img.style.removeProperty('width')
            }
          })
        } else {
          this.imageEls.forEach(img => {
            img.style.setProperty('aspect-ratio', 1)
            img.style.removeProperty('width')
            img.style.setProperty('max-width', '220px')
          })
        }
      }

      removeProduct (id) {
        const index = this.activeProducts.findIndex(p => p.id === id)
        this.activeProducts.splice(index, 1)
        this.cardCheckboxes.forEach(c => {
          if (c.value === id) {
            c.checked = false
          }
        })
        this.updatePreview()

        if (this.detailActive) {
          this.renderDetail()
        }
      }

      reset (
        closeDetail = false,
        deactivateSelection = false,
        unselect = false,
      ) {
        this.activeProducts = []
        this.updatePreview()

        if (deactivateSelection) {
          this.changeEl.checked = false
          this.changeEl.dispatchEvent(new Event('change'))
        }

        if (unselect) {
          this.cardCheckboxes.forEach(c => c.checked = false)
        }

        if (closeDetail) {
          this.toggleDetail(false)
        }
      }

      loadDetailElements (index) {
        return {
          imageFrame: this.detailPanelEl.querySelector(`[data-compare-image="${index}"]`),
          headFrame: this.detailPanelEl.querySelector(`[data-compare-head="${index}"]`),
          suctionPowerFrame: this.detailPanelEl.querySelector(`[data-compare-suction-power="${index}"]`),
          sizeFrame: this.detailPanelEl.querySelector(`[data-compare-size="${index}"]`),
          styleFrame: this.detailPanelEl.querySelector(`[data-compare-style="${index}"]`),
          materialFrame: this.detailPanelEl.querySelector(`[data-compare-material="${index}"]`),
          activitiesFrame: this.detailPanelEl.querySelector(`[data-compare-activities="${index}"]`),
        }
      }
    }

    customElements.define('femtis-collection-grid', FemtisCollectionGrid)
  })()
})()
